import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { GridColDef, GridColumnVisibilityModel, GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid';
import { useIntl } from 'react-intl';
import LanguageUtils from '../../../utils/LanguageUtils';
import TableUtils from '../../../utils/tableUtils';
import UrlConstants from '../../../constants/UrlConstants';
import CustomStringLink from '../../common/customStringLink';
import HelptextConstantsEnum from '../../../constants/helptextConstantsEnum';
import DataGridComponent from '../../common/dataGrid';
import CustomLink from '../../common/customLink';
import NumericFormat from 'react-number-format';

interface ReportLine005ListProps {
    classes?: any;
    checkSelection?: any;
    isLoading?: boolean;
    multipleSelection?: boolean;
    componentsProps?: any;
    onPageChange?: any;
    rows?: any;
    applyRowFn?: any;
    hiddenColumns?: Array<string>;
    onSortModelChange?: any;
    columnVisibilityModel?: GridColumnVisibilityModel;
    onColumnVisibilityModelChange?: any;
    setCurrentComponentId?: any;
    disableLinks?: boolean;
}

const messages = {
    invoiceYear: LanguageUtils.createMessage('Invoice Year'),
    invoiceMonth: LanguageUtils.createMessage('Invoice Month'),
    invoiceDate: LanguageUtils.createMessage('Invoice Date'),
    salesOrderNumber: LanguageUtils.createMessage('Sales Order Number'),
    d365InvoiceNumber: LanguageUtils.createMessage('D365 Invoice Number'),
    d365FeedbackStatus: LanguageUtils.createMessage('D365 Feedback Status'),
    salesPrice: LanguageUtils.createMessage('Sales Price'),
    d365SalesPrice: LanguageUtils.createMessage('D365 Sales Price'),
    diff: LanguageUtils.createMessage('Diff'),
    reconciliationStatus: LanguageUtils.createMessage('Reconciliation Status'),
    correctedStatus: LanguageUtils.createMessage('Corrected Status'),
    correctedText: LanguageUtils.createMessage('Corrected Text'),
    yesNoNeedHandling: LanguageUtils.createMessage('Need Handling'),
    customerNoName: LanguageUtils.createMessage('Customer'),
    invoiceType: LanguageUtils.createMessage('Invoice Type')
};

const ReportHeader005List = forwardRef((props: ReportLine005ListProps, ref: any): JSX.Element => {
    const { componentsProps, isLoading, checkSelection, onPageChange, rows, hiddenColumns, multipleSelection, onSortModelChange,
        columnVisibilityModel, onColumnVisibilityModelChange, setCurrentComponentId } = props;

    const defaultFn = (cellValues: GridRenderCellParams | GridValueGetterParams) => cellValues.row;

    const applyRowFn = props.applyRowFn || defaultFn;

    const dataGridRef = useRef();

    useImperativeHandle(ref, () => ({
        resetDataGridPage() {
            if(dataGridRef !== null && dataGridRef !== undefined && dataGridRef.current !== null && dataGridRef.current !== undefined) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                dataGridRef.current.resetPage();
            }
        }
    }));

    useEffect(() => {
        if(setCurrentComponentId !== undefined) {
            setCurrentComponentId(HelptextConstantsEnum.REPORT_ORDERS_NOT_INVOICES);
        }
    }, []);

    function generateColumns(): GridColDef[] {
        const intl = useIntl();

        return [
            {
                field: 'invoiceYear',
                headerName: intl.formatMessage(messages.invoiceYear),
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomStringLink align={'right'} value={applyRowFn(param)?.invoiceYear}
                        link={`/#/${UrlConstants.REPORT_LINE_005}/${applyRowFn(param)?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('invoiceYear', hiddenColumns)
            },
            {
                field: 'invoiceMonth',
                headerName: `${intl.formatMessage(messages.invoiceMonth)}`,
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomStringLink align={'right'} value={applyRowFn(param)?.invoiceMonth}
                        link={`/#/${UrlConstants.REPORT_LINE_005}/${applyRowFn(param)?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('invoiceMonth', hiddenColumns)
            },
            {
                field: 'invoiceDate',
                headerName: `${intl.formatMessage(messages.invoiceDate)}`,
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomStringLink align={'right'} value={applyRowFn(param)?.invoiceDate}
                        link={`/#/${UrlConstants.REPORT_LINE_005}/${applyRowFn(param)?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('invoiceDate', hiddenColumns)
            },
            {
                field: 'salesOrderNumber',
                headerName: `${intl.formatMessage(messages.salesOrderNumber)}`,
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomStringLink align={'right'} value={applyRowFn(param)?.salesOrderNumber}
                        link={`/#/${UrlConstants.REPORT_LINE_005}/${applyRowFn(param)?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('salesOrderNumber', hiddenColumns)
            },
            {
                field: 'd365InvoiceNumber',
                headerName: `${intl.formatMessage(messages.d365InvoiceNumber)}`,
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomStringLink align={'right'} value={applyRowFn(param)?.d365InvoiceNumber}
                        link={`/#/${UrlConstants.REPORT_LINE_005}/${applyRowFn(param)?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('d365InvoiceNumber', hiddenColumns)
            },
            {
                field: 'd365FeedbackStatus_name',
                headerName: `${intl.formatMessage(messages.d365FeedbackStatus)}`,
                flex: 0.5,
                align: 'left',
                headerAlign: 'left',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomStringLink align={'left'} value={applyRowFn(param)?.d365FeedbackStatus?.name}
                        link={`/#/${UrlConstants.REPORT_LINE_005}/${applyRowFn(param)?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('d365FeedbackStatus_name', hiddenColumns)
            },
            {
                field: 'salesPrice',
                headerName: `${intl.formatMessage(messages.salesPrice)}`,
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomLink align={'right'}
                        link={`/#/${UrlConstants.REPORT_LINE_005}/${applyRowFn(param)?.id}`}>
                        <NumericFormat value={applyRowFn(param)?.salesPrice} displayType={'text'} thousandSeparator={true} />
                    </CustomLink>;
                },
                hide: hiddenColumns && TableUtils.hideColumn('salesPrice', hiddenColumns)
            },
            {
                field: 'd365SalesPrice',
                headerName: `${intl.formatMessage(messages.d365SalesPrice)}`,
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomLink align={'right'}
                        link={`/#/${UrlConstants.REPORT_LINE_005}/${applyRowFn(param)?.id}`}>
                        <NumericFormat value={applyRowFn(param)?.d365SalesPrice} displayType={'text'} thousandSeparator={true} />
                    </CustomLink>;
                },
                hide: hiddenColumns && TableUtils.hideColumn('d365SalesPrice', hiddenColumns)
            },
            {
                field: 'diff',
                headerName: `${intl.formatMessage(messages.diff)}`,
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomLink align={'right'}
                        link={`/#/${UrlConstants.REPORT_LINE_005}/${applyRowFn(param)?.id}`}>
                        <NumericFormat value={applyRowFn(param)?.diff} displayType={'text'} thousandSeparator={true} />
                    </CustomLink>;
                },
                hide: hiddenColumns && TableUtils.hideColumn('diff', hiddenColumns)
            },
            {
                field: 'reconciliationStatus_name',
                headerName: `${intl.formatMessage(messages.reconciliationStatus)}`,
                flex: 0.5,
                align: 'left',
                headerAlign: 'left',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomStringLink align={'left'} value={applyRowFn(param)?.reconciliationStatus?.name}
                        link={`/#/${UrlConstants.REPORT_LINE_005}/${applyRowFn(param)?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('reconciliationStatus_name', hiddenColumns)
            },
            {
                field: 'correctedStatus_name',
                headerName: `${intl.formatMessage(messages.correctedStatus)}`,
                flex: 0.5,
                align: 'left',
                headerAlign: 'left',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomStringLink align={'left'} value={applyRowFn(param)?.correctedStatus?.name}
                        link={`/#/${UrlConstants.REPORT_LINE_005}/${applyRowFn(param)?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('correctedStatus_name', hiddenColumns)
            },
            {
                field: 'correctedText',
                headerName: `${intl.formatMessage(messages.correctedText)}`,
                flex: 0.5,
                align: 'left',
                headerAlign: 'left',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomStringLink align={'left'} value={applyRowFn(param)?.correctedText}
                        link={`/#/${UrlConstants.REPORT_LINE_005}/${applyRowFn(param)?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('correctedText', hiddenColumns)
            },
            {
                field: 'yesNoNeedHandling_name',
                headerName: `${intl.formatMessage(messages.yesNoNeedHandling)}`,
                flex: 0.5,
                align: 'left',
                headerAlign: 'left',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomStringLink align={'left'} value={applyRowFn(param)?.yesNoNeedHandling?.name}
                        link={`/#/${UrlConstants.REPORT_LINE_005}/${applyRowFn(param)?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('yesNoNeedHandling_name', hiddenColumns)
            },
            {
                field: 'customerNoName',
                headerName: `${intl.formatMessage(messages.customerNoName)}`,
                flex: 0.5,
                align: 'left',
                headerAlign: 'left',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomStringLink align={'left'} value={applyRowFn(param)?.customerNoName}
                        link={`/#/${UrlConstants.REPORT_LINE_005}/${applyRowFn(param)?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('customerNoName', hiddenColumns)
            },
            {
                field: 'invoiceType_name',
                headerName: `${intl.formatMessage(messages.invoiceType)}`,
                flex: 0.5,
                align: 'left',
                headerAlign: 'left',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomStringLink align={'left'} value={applyRowFn(param)?.invoiceType?.name}
                        link={`/#/${UrlConstants.REPORT_LINE_005}/${applyRowFn(param)?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('invoiceType_name', hiddenColumns)
            }
        ];
    }

    return (
        <DataGridComponent getRowId={(row) => row?.customerNoName + row?.amount + Math.random()} rows={rows} multipleSelection={multipleSelection} columns={generateColumns()}
            ref={dataGridRef} componentsProps={componentsProps} isLoading={isLoading}
            checkSelection={checkSelection} onPageChange={onPageChange} onSortModelChange={onSortModelChange}
            columnVisibilityModel={columnVisibilityModel} onColumnVisibilityModelChange={onColumnVisibilityModelChange} />
    );
});

export default ReportHeader005List;
